import { z } from "zod";

import type { GetReferenceDetailQuery, GetReferencesQuery } from "./gqls";

export type ReferenceRaw = GetReferencesQuery["references"][number];
export type ReferenceDetailRaw = GetReferenceDetailQuery["references"][number];

/** UI-ready list row. */
export interface ReferenceRow {
  id: string;
  seqId: string;
  title: string;
  shortCode: string;
  isbn: string;
  documentUrl: string | null;
  isDeactivated: boolean;
}

/** URL-state filter for the list (only an exclude-deactivated toggle in V1). */
export interface ReferenceListFilters {
  excludeDeactivated: boolean;
}

/** The single reference file (latest version) shown on the detail page. */
interface ReferenceFileUI {
  id: string;
  googleDriveFileId: string;
  /** Page images have been generated (gates the slide viewer). */
  hasImages: boolean;
  numPages: number | null;
  folioOffset: number;
}

/** Read-only detail shape. */
export interface ReferenceDetailUI {
  id: string;
  seqId: string;
  title: string;
  shortCode: string;
  isbn: string;
  authors: string[];
  publishers: string[];
  publishedDate: string | null;
  edition: string | null;
  documentUrl: string | null;
  evidencesCount: number;
  isDeactivated: boolean;
  file: ReferenceFileUI | null;
}

// ── Forms ───────────────────────────────────────────────────────────────────

/** Create/edit form. `id` present ⇒ update, absent ⇒ insert (single upsert mutation). */
export const zReferenceFormData = z.object({
  id: z.string().optional(),
  title: z.string().min(1),
  shortCode: z.string().min(1),
  isbn: z.string().min(1),
  documentUrl: z.string(),
  publisherIds: z.array(z.string()),
  newPublishers: z.array(
    z.object({
      name: z
        .string()
        .transform((name) => name.trim().replace(/\s+/g, " "))
        .pipe(
          z
            .string()
            .min(1)
            .refine((name) => [...name].length <= 255),
        ),
    }),
  ),
  edition: z.string(),
  publishedDate: z
    .string()
    .trim()
    .refine(
      (value) =>
        value === "" ||
        (/^\d{4}(?:-\d{2}(?:-\d{2})?)?$/.test(value) &&
          Number(value.slice(0, 4)) > 0 &&
          z.iso.date().safeParse(value.length === 4 ? `${value}-01-01` : value.length === 7 ? `${value}-01` : value)
            .success),
    ),
  authors: z.array(z.object({ name: z.string().min(1) })),
});
export type ReferenceFormData = z.infer<typeof zReferenceFormData>;

/** Add/replace a reference file from a Google Drive file id (or pasted Drive URL). */
export const zReferenceFileAddFormData = z.object({
  googleDriveFileId: z.string().min(1),
});
export type ReferenceFileAddFormData = z.infer<typeof zReferenceFileAddFormData>;

/** Edit the folio offset (string in the form, coerced to Int on submit). */
export const zReferenceFileEditFormData = z.object({
  folioOffset: z.string(),
});
export type ReferenceFileEditFormData = z.infer<typeof zReferenceFileEditFormData>;
